/**
 * Guided-demo funnel analytics.
 *
 * The event names are defined in `DEMO_EVENT_NAME` from `./types.ts`.
 */
import { trackEvent } from "@/lib/analytics/posthog";
import { DEMO_EVENT_NAME, type DemoEventName } from "./types";

export interface DemoAnalyticsPayload {
  name: DemoEventName;
  nicheSlug?: string;
  timeWindowDays?: number;
  resultKind?: string;
  durationBucket?: string;
  /** Freeform extra properties merged into the analytics event. */
  extra?: Record<string, string | number | boolean>;
}

/** Fire a single guided-demo funnel event. */
export function trackDemoEvent(payload: DemoAnalyticsPayload): void {
  const { name, extra, ...rest } = payload;

  trackEvent(name, {
    category: "guided_demo",
    ...rest,
    ...extra,
  });
}

/* -------------------------------------------------------------------------- */
/*  Convenience helpers (one per funnel step)                                 */
/* -------------------------------------------------------------------------- */

export function trackImpression(): void {
  trackDemoEvent({ name: DEMO_EVENT_NAME.IMPRESSION });
}

export function trackRunClicked(
  nicheSlug: string,
  timeWindowDays: number,
): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.RUN_CLICKED,
    nicheSlug,
    timeWindowDays,
  });
}

export function trackJobAccepted(nicheSlug: string): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.JOB_ACCEPTED,
    nicheSlug,
  });
}

export function trackResultShown(nicheSlug: string, resultKind: string): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.RESULT_SHOWN,
    nicheSlug,
    resultKind,
  });
}

export function trackDemoError(nicheSlug: string, errorCode: string): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.ERROR,
    nicheSlug,
    extra: { error_code: errorCode },
  });
}

export function trackCtaOpened(nicheSlug: string, resultKind: string): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.CTA_OPENED,
    nicheSlug,
    resultKind,
  });
}

export function trackBuildPromptOpened(
  nicheSlug: string,
  resultKind: string,
): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.BUILD_PROMPT_OPENED,
    nicheSlug,
    resultKind,
  });
}

export function trackBuildPromptCopied(
  nicheSlug: string,
  resultKind: string,
): void {
  trackDemoEvent({
    name: DEMO_EVENT_NAME.BUILD_PROMPT_COPIED,
    nicheSlug,
    resultKind,
  });
}
